import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { Unit } from '../../../models/entities/unit';
import { Membership } from '../../../models/entities/membership';
import { Tag } from '../../../models/entities/tag';
import { MemberListState, PickerExtras, PickerTag, PickerUnit, toKitUnits, UnitListService, UnitListState, UnitsQuery } from '../../../services/units/unit-list.service';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../growler/growler.service';
import { UnitPickerColumnsService } from '../../../services/unit-picker-columns.service';

export type PickerColumn = 'address' | 'tags' | 'balance' | 'pastDue' | 'autopay' | 'ownerEmails' | 'thumbnail';
export type PickerExtraColumn = { key: string; label: string };
export type PickerPerson = {
    id: number;
    name: string;
    address?: string;
    email?: string;
    phone?: string;
    tags: string[];
    neverLoggedIn?: boolean;
    href?: string;
    disabled?: boolean;
    disabledReason?: string;
};
export type PickerChange = { value: (number | string)[]; owners: (number | string)[]; count: number; ownerCount: number };
export type { UnitsQuery, PickerExtras, PickerTag, PickerUnit };

const MILL_CREEK_ORG = 4203;

/** A member with a real email who has never logged in. */
function neverLoggedIn(member: Membership): boolean
{
    const user = member.user;

    return !!user?.email && !user.hasAutogeneratedEmailAddress?.() && !user.lastLogin;
}

/** True when the user may see unit balances, which gates the Balance and Past due columns as V1 did. */
export function canSeeBalances(): boolean
{
    return LegFiJwtService.doesUserHaveModulePermission('charges', false) || LegFiJwtService.doesUserHaveModulePermission('unit', false);
}

function sameIds(a: number[] | null | undefined, b: number[] | null | undefined): boolean
{
    const left = a || [];
    const right = b || [];

    return left.length === right.length && left.every((id, i) => id === right[i]);
}

/**
 * What a save must add and remove when the picker's final list is the truth. A
 * preselected id the picker never showed (deleted, filtered out, still loading) is
 * never removed: the person could not have unpicked it.
 */
export function pickerDiff(initial: number[], final: number[], shown: Set<number>): { added: number[]; removed: number[] }
{
    const start = new Set(initial);
    const end = new Set(final);

    return {
        added: final.filter(id => !start.has(id)),
        removed: initial.filter(id => !end.has(id) && shown.has(id)),
    };
}

/** The money columns this user may see, out of the ones a page asks for. */
export function moneyColumns(...wanted: ('balance' | 'pastDue')[]): PickerColumn[]
{
    return canSeeBalances() ? wanted : [];
}

/**
 * ph-unit-picker with the org's units (and, for the Owners pick, members) loaded and
 * mapped, so each page only says which columns it shows and what it does with the pick.
 */
@UntilDestroy()
@Component({
    selector: 'app-unit-picker',
    templateUrl: './unit-picker.component.html',
    standalone: true,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitPickerComponent implements OnInit, OnChanges, OnDestroy
{
    @Input() pick: 'units' | 'owners' | 'both' = 'units';
    /** `pick="both"`: the tab it opens on. */
    @Input() defaultTab: 'units' | 'owners' = 'units';
    /** Adds the "Never logged in" filter chip: members with a real email and no login. */
    @Input() neverLoggedInFilter = false;
    /** Hard-coded camelCase per host (e.g. 'violationEditor'); saved column choices live under it. Empty = no memory. */
    @Input() pickerKey = '';
    @Input() columns: PickerColumn[] = [];
    @Input() extraColumns: PickerExtraColumn[] = [];
    @Input() unitExtras: ((unit: Unit) => PickerExtras) | null = null;
    /** Passed to UnitsService.getUnits, such as the voting filters or `unitIds`. */
    @Input() unitQuery: UnitsQuery = {};
    @Input() preselectedUnitIds: number[] = [];
    @Input() preselectedMemberIds: number[] = [];
    /** V1's doHideMillCreekLocked: drops units with a locked owner in Mill Creek (org 4203). */
    @Input() hideMillCreekLocked = false;
    /** A reason makes the unit listed but unpickable. */
    @Input() disableUnit: ((unit: Unit) => string | null) | null = null;
    /** Owners pick: a reason makes the member listed but unpickable. */
    @Input() disableMember: ((member: Membership) => string | null) | null = null;
    /** Owners pick: members missing this are blocked, and a click emits `blockedMember`. */
    @Input() requireEmail = false;
    @Input() requirePhone = false;
    /** Owners pick: `mailing` fills Address with the owner's mailing address (then a unit address), so same-name owners differ. */
    @Input() ownerAddress: 'units' | 'mailing' = 'units';
    /** Owners pick: false drops the name link to the member page, for a picker that sits on one. */
    @Input() linkOwners = true;
    @Input() canShowDeleted = !!LegFiJwtService.read()?.superUser;
    @Input() rowAction: string | null = null;
    /** Picks one unit: a click replaces the pick, and there is no Add all. */
    @Input() single = false;
    @Input() listHeight = 400;
    @Input() searchPlaceholder = '';
    @Input() disabled = false;
    /** `pick="both"` for sharing: a picked unit covers all its owners, now and later. The kit draws the has-access list and turns a unit into owner picks when one is unticked. */
    @Input() coverOwners = false;

    @Output() unitsChange = new EventEmitter<Unit[]>();
    @Output() unitIdsChange = new EventEmitter<number[]>();
    @Output() membersChange = new EventEmitter<Membership[]>();
    @Output() memberIdsChange = new EventEmitter<number[]>();
    @Output() rowActionClick = new EventEmitter<Unit>();
    @Output() blockedMember = new EventEmitter<Membership>();
    @Output() loaded = new EventEmitter<Unit[]>();
    /** The units could not load; `loaded` still fires with what did. */
    @Output() loadError = new EventEmitter<HttpErrorResponse>();

    units: PickerUnit[] = [];
    owners: PickerPerson[] = [];
    unitTags: PickerTag[] = [];
    ownerTags: PickerTag[] = [];
    unitIds: number[] = [];
    memberIds: number[] = [];
    /** Every column this user may turn on; `columns` is only what shows by default. */
    offeredColumns: PickerColumn[] = [];
    visibleColumns: string[] = [];

    unitsLoading = true;
    membersLoading = true;
    deletedLoading = false;
    unitsLoaded = 0;
    unitsTotal = 0;

    private _members: Membership[] = [];
    private _loadedUnits: Unit[] = [];
    private _baseUnits: Unit[] = [];
    private _deletedUnits: Unit[] = [];
    private _addedUnits: Unit[] = [];
    private _deletedRequested = false;
    private _unitsSub: Subscription | null = null;
    private _membersRequested = false;
    private _partialEmit = false;
    private _destroyed = false;
    private _columnsTouched = false;
    private _extraKeys = new Set<string>();
    private _selected: { units: Unit[]; ids: number[]; result: Unit[] } | null = null;
    private _hiddenUnitIds = new Set<number>();

    constructor(
            private _unitList: UnitListService,
            private _router: Router,
            private _growler: GrowlerService,
            private _cdr: ChangeDetectorRef,
            private _pickerColumns: UnitPickerColumnsService,
    ) {
    }

    get ownerContact(): 'email' | 'phone' {
        return this.requirePhone && !this.requireEmail ? 'phone' : 'email';
    }

    /** Nothing to show yet: no unit page has landed. Later pages, and the owners, fill in behind the picker. */
    get firstLoad(): boolean {
        const unitsPending = this.unitsLoading && !this._baseUnits.length;

        return this.pick === 'owners' ? this.membersLoading : unitsPending || (this.pick === 'both' && this.membersLoading);
    }

    get loadingLabel(): string {
        return this.unitsLoading && this._baseUnits.length ? `Loading ${this.unitsLoaded.toLocaleString('en-US')} of ${this.unitsTotal.toLocaleString('en-US')} units` : '';
    }

    get locked(): boolean {
        return this.disabled || this.deletedLoading;
    }

    get selectedUnits(): Unit[] {
        const cached = this._selected;
        if (cached && cached.units === this._loadedUnits && cached.ids === this.unitIds) {
            return cached.result;
        }
        const chosen = new Set(this.unitIds);
        const result = this._loadedUnits.filter(u => chosen.has(u.id) && !this._hiddenUnitIds.has(u.id));
        this._selected = { units: this._loadedUnits, ids: this.unitIds, result };

        return result;
    }

    get selectedMembers(): Membership[] {
        const chosen = new Set(this.memberIds);

        return this._members.filter(m => chosen.has(m.id));
    }

    /** Distinct people the pick reaches: every owner of each picked unit, plus each picked owner. */
    get homeownerCount(): number {
        const ids = new Set(this.memberIds);
        this.selectedUnits.forEach(u => (u.owners || []).forEach(o => o.memberId && ids.add(o.memberId)));

        return ids.size;
    }

    get allUnits(): Unit[] {
        return this._loadedUnits;
    }

    /** Ids of the units the person could see and unpick; pass to pickerDiff. */
    get shownUnitIds(): Set<number> {
        return new Set(this.units.filter(u => !u.disabled).map(u => u.id));
    }

    ngOnInit(): void {
        this.unitIds = [...(this.preselectedUnitIds || [])];
        this.memberIds = [...(this.preselectedMemberIds || [])];
        this.syncColumns();
        this.restoreColumns();
        this.loadMembers();
        this.loadUnits();
    }

    ngOnDestroy(): void {
        this._destroyed = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.columns || changes.extraColumns) {
            this.syncColumns();
        }
        if ((changes.pick || changes.hideMillCreekLocked) && !(changes.pick || changes.hideMillCreekLocked).firstChange) {
            this.loadMembers();
        }
        if (changes.pick && !changes.pick.firstChange && this.needsUnits && !this._unitsSub) {
            this.loadUnits();
        }
        if (changes.preselectedUnitIds && !changes.preselectedUnitIds.firstChange && !sameIds(changes.preselectedUnitIds.previousValue, this.preselectedUnitIds)) {
            this.unitIds = [...(this.preselectedUnitIds || [])];
        }
        if (changes.preselectedMemberIds && !changes.preselectedMemberIds.firstChange && !sameIds(changes.preselectedMemberIds.previousValue, this.preselectedMemberIds)) {
            this.memberIds = [...(this.preselectedMemberIds || [])];
        }
        if (changes.unitQuery && !changes.unitQuery.firstChange) {
            this.reload();
            return;
        }
        if ((changes.columns || changes.extraColumns || changes.disableUnit || changes.disableMember || changes.unitExtras || changes.neverLoggedInFilter) && !this.firstLoad) {
            this.build();
        }
    }

    onColumnVisibility(keys: string[]): void {
        this.visibleColumns = keys;
        this._columnsTouched = true;
        if (this.pickerKey) {
            this._pickerColumns.save(this.pickerKey, keys);
        }
    }

    private restoreColumns(): void {
        if (!this.pickerKey) {
            return;
        }
        this._pickerColumns.watch(this.pickerKey).pipe(untilDestroyed(this)).subscribe(keys => {
            if (!keys || sameIds(keys as unknown as number[], this.visibleColumns as unknown as number[])) {
                return;
            }
            this.visibleColumns = [...keys];
            this._columnsTouched = true;
            this.syncColumns();
            this._cdr.markForCheck();
        });
    }

    private syncColumns(): void {
        const all: PickerColumn[] = ['address', 'tags', ...moneyColumns('balance', 'pastDue'), 'autopay', 'ownerEmails'];
        const shown = this.columns || [];
        const extras = (this.extraColumns || []).map(c => `extra:${c.key}`);
        this.offeredColumns = [...shown, ...all.filter(c => !shown.includes(c))];
        if (!this._columnsTouched) {
            this.visibleColumns = [...shown, ...extras];
        } else {
            const known = new Set<string>([...this.offeredColumns, ...extras]);
            const kept = this.visibleColumns.filter(k => known.has(k));
            this.visibleColumns = [...kept, ...extras.filter(k => !this._extraKeys.has(k) && !kept.includes(k))];
        }
        this._extraKeys = new Set(extras);
    }

    /** Loads the units again for the current query, keeping the selection. */
    reload(): void {
        this._baseUnits = [];
        this._deletedUnits = [];
        this.mergeUnits();
        this._deletedRequested = false;
        this.loadUnits();
        this._cdr.markForCheck();
    }

    /** Whether a loaded member has the contact this picker requires (email or phone). */
    canReach(memberId: number): boolean {
        const member = this._members.find(m => m.id === memberId);

        return !!member && !this.missingContact(member);
    }

    /** After the page fixed a blocked member (added the email), swaps them in and picks them. */
    replaceMember(member: Membership, pick = true): void {
        this._members = this._members.map(m => m.id === member.id ? member : m);
        if (pick && !this.memberIds.includes(member.id)) {
            this.memberIds = [...this.memberIds, member.id];
        }
        this.build();
        this.emitMembers();
    }

    /** Adds a unit the page just created and picks it. */
    addUnit(unit: Unit, pick = true): void {
        this._addedUnits = [...this._addedUnits.filter(u => u.id !== unit.id), unit];
        this.mergeUnits();
        if (pick && !this.unitIds.includes(unit.id)) {
            this.unitIds = this.single ? [unit.id] : [...this.unitIds, unit.id];
        }
        this.build();
        this.emitUnits();
    }

    clear(): void {
        this.unitIds = [];
        this.memberIds = [];
        this.emitUnits();
        this.emitMembers();
        this._cdr.markForCheck();
    }

    onChange(detail: PickerChange): void {
        const blockedUnits = new Set(this.units.filter(u => u.disabled).map(u => u.id));
        const blockedOwners = new Set(this.owners.filter(o => o.disabled).map(o => o.id));
        const units = (detail.value || []).map(id => Number(id)).filter(id => !blockedUnits.has(id));
        const members = (detail.owners || []).map(id => Number(id)).filter(id => !blockedOwners.has(id));
        const unitsMoved = units.length !== this.unitIds.length || units.some((id, i) => id !== this.unitIds[i]);
        const membersMoved = members.length !== this.memberIds.length || members.some((id, i) => id !== this.memberIds[i]);
        this.unitIds = units;
        this.memberIds = members;
        if (unitsMoved) {
            this.emitUnits();
        }
        if (membersMoved) {
            this.emitMembers();
        }
        this._cdr.markForCheck();
    }

    onShowDeleted(show: boolean): void {
        if (!show || this._deletedRequested) {
            return;
        }
        this._deletedRequested = true;
        this.deletedLoading = true;
        this._cdr.markForCheck();
        this.loadDeleted();
    }

    onRowAction(id: number | string): void {
        const unit = this._loadedUnits.find(u => u.id === Number(id));
        if (unit) {
            this.rowActionClick.emit(unit);
        }
    }

    onNavigate(href: string): void {
        this._router.navigateByUrl(href);
    }

    onBlocked(detail: { id: number | string; kind: 'unit' | 'owner' }): void {
        if (detail.kind !== 'owner') {
            return;
        }
        const member = this._members.find(m => m.id === Number(detail.id));
        if (member) {
            this.blockedMember.emit(member);
        }
    }

    private emitUnits(): void {
        const selected = this.selectedUnits;
        this._partialEmit = this._partialEmit || (this.unitsLoading && selected.length < this.unitIds.length);
        this.unitIdsChange.emit([...this.unitIds]);
        this.unitsChange.emit([...selected]);
    }

    /** Outputs that fire off a load wait a microtask, so a cached list replayed during init reaches a host whose view and @ViewChild are ready. */
    private later(emit: () => void): void {
        Promise.resolve().then(() => {
            if (!this._destroyed) {
                emit();
            }
        });
    }

    private get needsMembers(): boolean {
        return this.pick !== 'units' || (this.hideMillCreekLocked && LegFiJwtService.read()?.orgId === MILL_CREEK_ORG);
    }

    /** Owner rows come from members alone, which carry their own unit names. */
    private get needsUnits(): boolean {
        return this.pick !== 'owners';
    }

    private emitMembers(): void {
        this.memberIdsChange.emit([...this.memberIds]);
        this.membersChange.emit(this.selectedMembers);
    }

    private missingContact(member: Membership): string | null {
        if (this.requireEmail && !member.user?.email) {
            return 'No email on file. Click to add one.';
        }

        return this.requirePhone && !member.profile?.phone ? 'No phone number on file. Click to add one.' : null;
    }

    private ownerAddressOf(m: Membership): string {
        if (this.ownerAddress !== 'mailing') {
            return m.unitsString;
        }
        const mailing = [m.profile?.address1, m.profile?.address2].filter(Boolean).join(' ');

        return mailing || m.owners?.find(o => o.unit?.addressString)?.unit.addressString || m.unitsString;
    }

    private loadMembers(): void {
        if (this._membersRequested || !this.needsMembers) {
            return;
        }
        this._membersRequested = true;
        this._unitList.members$().pipe(untilDestroyed(this)).subscribe((state: MemberListState) => {
            this._members = state.members;
            this.membersLoading = false;
            this.build();
            if (state.error && this.pick !== 'units') {
                this._growler.fromErrorResponse(state.error, 'Owners could not be loaded. Reload the page and try again.');
            }
        });
    }

    private loadUnits(): void {
        if (!this.needsUnits) {
            this.unitsLoading = false;
            this.build();
            return;
        }
        this.unitsLoading = true;
        let first = true;
        this._unitsSub?.unsubscribe();
        this._unitsSub = this._unitList.units$(this.unitQuery).pipe(untilDestroyed(this)).subscribe((state: UnitListState) => {
            this._baseUnits = state.units;
            this.mergeUnits();
            this.unitsLoaded = state.loaded;
            this.unitsTotal = state.total;
            if (!state.done && state.units.length) {
                this.build();
            }
            if (state.done) {
                this.unitsLoading = false;
                this.build();
                if (state.error) {
                    if (this.loadError.observed) {
                        this.loadError.emit(state.error);
                    } else {
                        this._growler.fromErrorResponse(state.error, 'Not every unit loaded. Reload the page to see them all.');
                    }
                }
                if (first) {
                    first = false;
                    this.later(() => this.loaded.emit(this._loadedUnits));
                }
                if (this._partialEmit) {
                    this._partialEmit = false;
                    this.later(() => this.unitsChange.emit([...this.selectedUnits]));
                }
            }
            this._cdr.markForCheck();
        });
    }

    private loadDeleted(): void {
        this._unitList.units$({ ...(this.unitQuery || {}), showDeleted: true }).pipe(untilDestroyed(this)).subscribe((state: UnitListState) => {
            if (!state.done) {
                return;
            }
            this._deletedUnits = state.units.filter(u => !!u.deletedAt);
            this.deletedLoading = false;
            this.mergeUnits();
            this.build();
            if (state.error) {
                this._growler.fromErrorResponse(state.error, 'Deleted units could not be loaded.');
            }
        });
    }

    private mergeUnits(): void {
        const seen = new Set<number>();
        this._loadedUnits = [...this._baseUnits, ...this._deletedUnits, ...this._addedUnits].filter((u) => {
            if (seen.has(u.id)) {
                return false;
            }
            seen.add(u.id);

            return true;
        });
    }

    private build(): void {
        if (this.unitsLoading && !this._baseUnits.length) {
            return;
        }
        const byId = new Map(this._members.map(m => [m.id, m]));
        const millCreek = this.hideMillCreekLocked && LegFiJwtService.read()?.orgId === MILL_CREEK_ORG;
        this._hiddenUnitIds = new Set(millCreek
                ? this._loadedUnits.filter(u => (u.owners || []).some(o => (byId.get(o.memberId) || o.membership)?.locked)).map(u => u.id)
                : []);
        this._selected = null;
        const visible = this._hiddenUnitIds.size ? this._loadedUnits.filter(u => !this._hiddenUnitIds.has(u.id)) : this._loadedUnits;

        const kit = toKitUnits(visible, {
            members: this._members,
            thumbnail: this.columns.includes('thumbnail'),
            disableUnit: this.disableUnit,
            unitExtras: this.unitExtras,
        });
        const never = new Set(this.neverLoggedInFilter ? this._members.filter(neverLoggedIn).map(m => m.id) : []);
        this.units = never.size ? kit.units.map(u => ({ ...u, owners: u.owners.map(o => o.id !== undefined && never.has(o.id) ? { ...o, neverLoggedIn: true } : o) })) : kit.units;
        const unitTags = new Map(kit.unitTags.map(t => [t.id, t]));
        const ownerTags = new Map(kit.ownerTags.map(t => [t.id, t]));
        const tagIds = (tags: Tag[] | undefined, into: Map<string, PickerTag>): string[] => (tags || [])
                .filter(t => !!t.tag)
                .map((t) => {
                    into.set(t.tag, into.get(t.tag) || { id: t.tag, label: t.tag, color: t.taggableColor });

                    return t.tag;
                });

        this.owners = this._members.map((m) => {
            const reason = this.disableMember?.(m) || this.missingContact(m);
            const address = this.ownerAddressOf(m);

            return {
                id: m.id,
                name: m.name || `Member ${m.id}`,
                ...(address ? { address } : {}),
                ...(m.user?.email ? { email: m.user.email } : {}),
                ...(m.profile?.phone ? { phone: m.profile.phone } : {}),
                tags: tagIds(m.tags, ownerTags),
                ...(never.has(m.id) ? { neverLoggedIn: true } : {}),
                ...(this.linkOwners ? { href: `/app/members/${m.id}` } : {}),
                ...(reason ? { disabled: true, disabledReason: reason } : {}),
            };
        });

        this.unitTags = Array.from(unitTags.values()).sort((a, b) => a.label.localeCompare(b.label));
        this.ownerTags = Array.from(ownerTags.values()).sort((a, b) => a.label.localeCompare(b.label));
        this._cdr.markForCheck();
    }
}
